import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import {
  Add as AddIcon,
  Autorenew as RegenerateIcon,
  Cake as CakeIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Fastfood as FastfoodIcon,
  Label as LabelIcon,
  LocalDrink as DrinkIcon,
  Print as PrintIcon,
  Restaurant as RestaurantIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { dateMask } from "../../common/dateMask";
import dayjs from "dayjs";
import { printLabel } from "../../utils/printService";

const tableRowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
    },
  }),
  removed: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 },
  },
};

const PREFAB_CONFIGS = {
  SUPER_BASIC: { name: "Super Básica", icon: LabelIcon, color: "#6B7280" },
  BASIC: { name: "Básica", icon: LabelIcon, color: "#6B7280" },
  WITH_STORAGE: {
    name: "Com Conservação",
    icon: RestaurantIcon,
    color: "#10B981",
  },
  WITH_BARCODE: { name: "Com Código Barras", icon: CakeIcon, color: "#2563EB" },
  WITH_QRCODE: { name: "Com QR Code", icon: DrinkIcon, color: "#F59E0B" },
  COMPLETE: { name: "Completa", icon: FastfoodIcon, color: "#8B5CF6" },
};

const getPrefabConfig = (prefabId) => {
  return PREFAB_CONFIGS[prefabId] || PREFAB_CONFIGS.BASIC;
};

const SimpleLabelTable = ({
  labels,
  onEditLabel,
  onDeleteLabel,
  onAddLabel,
  onCancelLabel,
  onRegenerateLabel,
  loading,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("manufactureDate");
  const [order, setOrder] = useState("desc");
  const [expandedExpiredAlert, setExpandedExpiredAlert] = useState({});
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [labelToPrint, setLabelToPrint] = useState(null);
  const [printQuantity, setPrintQuantity] = useState(1);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    setPage(0);
  };

  const getComparator = (orderBy, order) => {
    const isAscending = order === "asc";
    return isAscending
      ? (a, b) => compareValues(a, b, orderBy)
      : (a, b) => -compareValues(a, b, orderBy);
  };

  const compareValues = (a, b, orderBy) => {
    if (orderBy === "prefab") {
      const prefabA = getPrefabConfig(a.prefab).name;
      const prefabB = getPrefabConfig(b.prefab).name;
      return prefabA.localeCompare(prefabB);
    }

    const valueA = a[orderBy];
    const valueB = b[orderBy];

    if (valueA == null) return -1;
    if (valueB == null) return 1;

    const isDateField =
      orderBy === "manufactureDate" || orderBy === "expirationDate";
    if (isDateField) {
      return new Date(valueA) - new Date(valueB);
    }

    return String(valueA).localeCompare(String(valueB));
  };

  const sortedData = useMemo(() => {
    const hasNoLabels = !labels || labels.length === 0;
    if (hasNoLabels) {
      return [];
    }

    const sorted = [...labels].sort(getComparator(orderBy, order));
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return sorted.slice(startIndex, endIndex);
  }, [labels, order, orderBy, page, rowsPerPage]);

  const headCells = [
    { id: "productName", label: "Produto", align: "left" },
    { id: "prefab", label: "Modelo", align: "left" },
    { id: "batchNumber", label: "Lote", align: "left" },
    { id: "manufactureDate", label: "Data Fabricação", align: "left" },
    { id: "expirationDate", label: "Data Validade", align: "left" },
    { id: "netWeight", label: "Peso Líquido", align: "center" },
    { id: "barcode", label: "Código de Barras", align: "left" },
    { id: "actions", label: "Ações", align: "center", disableSorting: true },
  ];

  const renderSortableHeaderCell = (headCell) => {
    const { id, label, align, disableSorting } = headCell;

    return (
      <TableCell key={id} align={align || "left"}>
        {disableSorting ? (
          label
        ) : (
          <TableSortLabel
            active={orderBy === id}
            direction={orderBy === id ? order : "asc"}
            onClick={() => handleRequestSort(id)}
          >
            {label}
          </TableSortLabel>
        )}
      </TableCell>
    );
  };

  const getExpirationStatus = (expirationDate) => {
    const today = dayjs();
    const expiration = dayjs(expirationDate);
    const daysUntilExpiration = expiration.diff(today, "days");

    const isExpired = daysUntilExpiration < 0;
    if (isExpired) {
      return { status: "expired", color: "error", label: "Vencido" };
    }

    const isExpiringSoon = daysUntilExpiration <= 3;
    if (isExpiringSoon) {
      return {
        status: "warning",
        color: "warning",
        label: `${daysUntilExpiration} dia(s)`,
      };
    }

    return { status: "valid", color: "success", label: "Válido" };
  };

  const toggleExpiredAlert = (labelId) => {
    setExpandedExpiredAlert((prev) => ({
      ...prev,
      [labelId]: !prev[labelId],
    }));
  };

  const handleOpenPrintDialog = (label) => {
    setLabelToPrint(label);
    setPrintQuantity(1);
    setPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
    setLabelToPrint(null);
    setPrintQuantity(1);
  };

  const handlePrintLabel = async () => {
    const hasNoLabel = !labelToPrint;
    if (hasNoLabel) {
      return;
    }

    try {
      console.log("Attempting to print label via WebSocket Print Service...");

      await printLabel(labelToPrint, printQuantity);

      console.log("WebSocket label print successful");
      handleClosePrintDialog();
    } catch (error) {
      console.error("WebSocket Print Error:", error);
      alert(`Erro ao imprimir: ${error.message}`);
    }
  };

  const handleQuantityChange = (event) => {
    const value = event.target.value;
    const parsedValue = parseInt(value, 10);

    const isValidNumber = !isNaN(parsedValue) && parsedValue > 0;
    if (isValidNumber) {
      setPrintQuantity(parsedValue);
      return;
    }

    const isEmpty = value === "";
    if (isEmpty) {
      setPrintQuantity("");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.5 }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha("#d97706", 0.1)}`,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${alpha("#d97706", 0.1)}`,
            background: `linear-gradient(135deg, ${alpha("#d97706", 0.03)} 0%, ${alpha("#f59e0b", 0.06)} 100%)`,
          }}
        >
          <Typography variant="h6" component="h2" fontWeight={600}>
            Lista de Etiquetas
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#d97706", 0.7), fontWeight: 500 }}>
            Exibindo {labels.length} etiquetas
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 4 }}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </Box>
        ) : labels.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: alpha("#d97706", 0.06),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <LabelIcon sx={{ fontSize: 40, color: alpha("#d97706", 0.4) }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhuma etiqueta encontrada
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 400, mb: 3 }}
            >
              Tente ajustar seus filtros ou adicione a primeira etiqueta
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAddLabel}
              sx={{
                background: `linear-gradient(135deg, #d97706 0%, #f59e0b 100%)`,
                boxShadow: `0 4px 12px ${alpha("#d97706", 0.3)}`,
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha("#d97706", 0.4)}`,
                },
              }}
            >
              Nova Etiqueta
            </Button>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 800 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>{headCells.map(renderSortableHeaderCell)}</TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {sortedData.map((label, index) => {
                      const expirationStatus = getExpirationStatus(
                        label.expirationDate
                      );
                      const isExpired = expirationStatus.status === "expired";
                      const isAlertExpanded =
                        expandedExpiredAlert[label.id] || false;

                      return (
                        <React.Fragment key={label.id}>
                          <motion.tr
                            initial="hidden"
                            animate="visible"
                            exit="removed"
                            variants={tableRowVariants}
                            custom={index}
                            component={motion.tr}
                            layoutId={`label-row-${label.id}`}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {isExpired && (
                                  <WarningIcon
                                    sx={{ color: "error.main", fontSize: 20 }}
                                  />
                                )}
                                <Typography variant="body2" fontWeight="medium">
                                  {label.productName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const prefabConfig = getPrefabConfig(
                                  label.prefab
                                );
                                const PrefabIcon = prefabConfig.icon;

                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <PrefabIcon
                                      sx={{
                                        fontSize: 20,
                                        color: prefabConfig.color,
                                      }}
                                    />
                                    <Typography variant="body2">
                                      {prefabConfig.name}
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={label.batchNumber}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: alpha("#d97706", 0.3),
                                  color: "#d97706",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {dateMask(label.manufactureDate)}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {dateMask(label.expirationDate)}
                                <Chip
                                  label={expirationStatus.label}
                                  size="small"
                                  color={expirationStatus.color}
                                  sx={{ ml: 1 }}
                                />
                                {isExpired && (
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleExpiredAlert(label.id)}
                                  >
                                    <WarningIcon
                                      color="error"
                                      fontSize="small"
                                    />
                                  </IconButton>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={label.netWeight}
                                size="small"
                                color="secondary"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                              >
                                {label.barcode || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Imprimir">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => handleOpenPrintDialog(label)}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onEditLabel(label)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onDeleteLabel(label.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </motion.tr>

                          {isExpired && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                                <Collapse in={isAlertExpanded}>
                                  <Alert
                                    severity="error"
                                    icon={<WarningIcon />}
                                    sx={{ m: 1, borderRadius: 1 }}
                                  >
                                    <AlertTitle>
                                      Etiqueta Vencida - Ação Necessária
                                    </AlertTitle>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                      Esta etiqueta está vencida desde{" "}
                                      {dateMask(label.expirationDate)}. Escolha
                                      uma ação:
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<CancelIcon />}
                                        onClick={() => onCancelLabel(label)}
                                      >
                                        Cancelar Etiqueta
                                      </Button>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<RegenerateIcon />}
                                        onClick={() => onRegenerateLabel(label)}
                                      >
                                        Gerar Novo Lote
                                      </Button>
                                    </Box>
                                  </Alert>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={labels.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Registros por Página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>

      <Dialog open={printDialogOpen} onClose={handleClosePrintDialog}>
        <DialogTitle>Imprimir Etiqueta</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Produto: <strong>{labelToPrint?.productName}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Lote: <strong>{labelToPrint?.batchNumber}</strong>
            </Typography>
            <TextField
              fullWidth
              label="Quantidade"
              type="number"
              value={printQuantity}
              onChange={handleQuantityChange}
              inputProps={{ min: 1 }}
              variant="outlined"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog}>Cancelar</Button>
          <Button
            onClick={handlePrintLabel}
            variant="contained"
            color="primary"
            disabled={!printQuantity || printQuantity < 1}
          >
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default SimpleLabelTable;
